import { Map as ImmutableMap, List, Record, fromJS } from 'immutable';
import { pipe } from 'ramda';
import {
  ADD_ENABLE_PWD,
  ADD_PWD,
  CLOSE_EDIT_USER_DIALOGUE,
  CLOSE_ENABLE_PWD_DIALOGUE,
  DELETE_ENABLE_PWD,
  DELETE_PWD,
  FETCH_GLOBAL_ENABLE_PWDLIST,
  FETCH_GLOBAL_PWDLIST,
  LOAD_ENABLE_PWD,
  LOAD_PWDLIST,
  OPEN_EDIT_USER_DIALOGUE,
  OPEN_ENABLE_PWD_DIALOGUE,
  SAVE_AUTHENTICATION,
  SORT_ROWS_ENABLE_PWD,
  SORT_ROWS_PWD,
  UPDATE_ENABLE_PWD,
  UPDATE_PWD,
} from './constants';

const InitialState = Record({
  pwdList: new List(),
  globalPwdList: new List(),
  initialPwdList: new List(),
  enablePwdList: new List(),
  globalEnablePwdList: new List(),
  initialEnablePwdList: new List(),
  editUserDialogue: new ImmutableMap({
    data: null,
    open: false,
  }),
  editEnablePwdDialogue: new ImmutableMap({
    data: null,
    open: false,
  }),
  settingsLoaded: false,
  pwdListLoading: false,
  enablePwdListLoading: false,
});

const initialState = new InitialState();

function deviceCredentialsReducer(state = initialState, action = undefined) {
  switch (action.type) {
    case `${LOAD_PWDLIST}_START`: {
      return state.set('pwdListLoading', true);
    }
    case `${LOAD_PWDLIST}_SUCCESS`: {
      const { datalist, loadData } = action.payload;
      if (loadData) {
        const immutableData = fromJS(datalist.data);
        return state
          .set('pwdList', immutableData)
          .set('initialPwdList', immutableData)
          .set('settingsLoaded', true)
          .set('pwdListLoading', false);
      }
      return state;
    }
    case `${LOAD_PWDLIST}_ERROR`: {
      return state.set('pwdListLoading', false);
    }
    case `${FETCH_GLOBAL_PWDLIST}_SUCCESS`: {
      const { datalist } = action.payload;
      const immutableData = fromJS(datalist.data);
      return state.set('globalPwdList', immutableData);
    }
    case `${LOAD_ENABLE_PWD}_START`: {
      return state.set('enablePwdListLoading', true);
    }
    case `${LOAD_ENABLE_PWD}_SUCCESS`: {
      const { datalist, loadData } = action.payload;
      if (loadData) {
        const immutableData = fromJS(datalist.data);
        return state
          .set('enablePwdList', immutableData)
          .set('initialEnablePwdList', immutableData)
          .set('enablePwdListLoading', false);
      }
      return state;
    }
    case `${LOAD_ENABLE_PWD}_ERROR`: {
      return state.set('enablePwdListLoading', false);
    }
    case `${FETCH_GLOBAL_ENABLE_PWDLIST}_SUCCESS`: {
      const { datalist } = action.payload;
      const immutableData = fromJS(datalist.data);
      return state.set('globalEnablePwdList', immutableData);
    }
    case `${SORT_ROWS_PWD}_SUCCESS`:
      return state.set('pwdList', fromJS(action.payload.data));
    case `${SORT_ROWS_ENABLE_PWD}_SUCCESS`:
      return state.set('enablePwdList', fromJS(action.payload.data));
    case `${ADD_PWD}_SUCCESS`:
      return state
        .set('pwdList', fromJS(action.payload.data))
        .set('editUserDialogue', new ImmutableMap({ open: false, data: null }));
    case `${DELETE_PWD}_SUCCESS`:
      return state.set('pwdList', fromJS(action.payload.data));
    case `${ADD_ENABLE_PWD}_SUCCESS`:
      return state
        .set('enablePwdList', fromJS(action.payload.data))
        .setIn(['editEnablePwdDialogue', 'open'], false);
    case `${DELETE_ENABLE_PWD}_SUCCESS`:
      return state.set('enablePwdList', fromJS(action.payload.data));
    case OPEN_EDIT_USER_DIALOGUE: {
      const { data, type } = action.payload;
      return state.set(
        'editUserDialogue',
        new ImmutableMap({
          open: true,
          data: new ImmutableMap({
            ...data,
            expirationDate: new ImmutableMap(data.expirationDate),
            password: null,
          }),
          type,
        }),
      );
    }
    case OPEN_ENABLE_PWD_DIALOGUE: {
      const { data, type } = action.payload;
      return state.set(
        'editEnablePwdDialogue',
        new ImmutableMap({
          open: true,
          data: new ImmutableMap({
            ...data,
            expirationDate: new ImmutableMap(data.expirationDate),
            password: null,
          }),
          type,
        }),
      );
    }
    case CLOSE_EDIT_USER_DIALOGUE:
      return state.set(
        'editUserDialogue',
        new ImmutableMap({ open: false, data: null }),
      );
    case CLOSE_ENABLE_PWD_DIALOGUE:
      return state.set(
        'editEnablePwdDialogue',
        new ImmutableMap({ open: false, data: null }),
      );
    case `${UPDATE_PWD}_SUCCESS`: {
      const {
        id,
        syslog,
        network,
        notes,
        password,
        expirationDate,
        system,
        excludeNetworks,
      } = action.payload;
      const pwdList = state.get('pwdList');
      const closedDialogueState = state.set(
        'editUserDialogue',
        new ImmutableMap({ open: false, data: null }),
      );
      const foundIndex = pwdList.findIndex((row) => row.get('id') === id);
      if (foundIndex > -1) {
        const passwordChanged =
          typeof password === 'string' && password.length > 0;
        return pipe(
          (s) =>
            passwordChanged
              ? s.setIn(['pwdList', foundIndex, 'password'], password)
              : s,
          (s) =>
            passwordChanged && !system
              ? s.setIn(['pwdList', foundIndex, 'custom'], true)
              : s,
          (s) =>
            s.setIn(
              ['pwdList', foundIndex, 'expirationDate'],
              expirationDate.enabled
                ? new ImmutableMap({
                    enabled: expirationDate.enabled,
                    value: expirationDate.value,
                  })
                : new ImmutableMap({ enabled: false }),
            ),
        )(
          closedDialogueState
            .setIn(['pwdList', foundIndex, 'syslog'], syslog)
            .setIn(['pwdList', foundIndex, 'network'], network)
            .setIn(['pwdList', foundIndex, 'notes'], notes)
            .setIn(['pwdList', foundIndex, 'excludeNetworks'], excludeNetworks),
        );
      }
      return state;
    }
    case `${UPDATE_ENABLE_PWD}_SUCCESS`: {
      const {
        id,
        notes,
        password,
        expirationDate,
        system,
        excludeNetworks,
        includeNetworks,
      } = action.payload;
      const enablePwdList = state.get('enablePwdList');
      const closedDialogueState = state.set(
        'editEnablePwdDialogue',
        new ImmutableMap({ open: false, data: null }),
      );
      const foundIndex = enablePwdList.findIndex((row) => row.get('id') === id);
      if (foundIndex > -1) {
        const passwordChanged =
          typeof password === 'string' && password.length > 0;
        const update = pipe(
          (s) =>
            passwordChanged
              ? s.setIn(['enablePwdList', foundIndex, 'password'], password)
              : s,
          (s) =>
            passwordChanged && !system
              ? s.setIn(['enablePwdList', foundIndex, 'custom'], true)
              : s,
          (s) =>
            s.setIn(
              ['enablePwdList', foundIndex, 'expirationDate'],
              expirationDate.enabled
                ? new ImmutableMap({
                    enabled: expirationDate.enabled,
                    value: expirationDate.value,
                  })
                : new ImmutableMap({ enabled: false }),
            ),
        )(
          closedDialogueState
            .setIn(['enablePwdList', foundIndex, 'notes'], notes)
            .setIn(
              ['enablePwdList', foundIndex, 'excludeNetworks'],
              excludeNetworks,
            )
            .setIn(
              ['enablePwdList', foundIndex, 'includeNetworks'],
              includeNetworks,
            ),
        );
        return update;
      }
      return state;
    }
    case `${SAVE_AUTHENTICATION}_SUCCESS`: {
      const { pwdList, enablePwdList } = action.payload;
      return state
        .set('initialPwdList', fromJS(pwdList))
        .set('pwdList', fromJS(pwdList))
        .set('initialEnablePwdList', fromJS(enablePwdList))
        .set('enablePwdList', fromJS(enablePwdList));
    }
    default:
      return state;
  }
}

export default deviceCredentialsReducer;
